import * as React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@fluentui/react-components";
import useResizeCount from "../hooks/useResizeCount";

const fadeIn = {
  from: {
    opacity: 0,
    transform: "translateY(10px)",
  },
  to: {
    opacity: 1,
    transform: "translateY(0px)",
  },
};

const bounceLeft = {
  "0%, 100%": { transform: "translateX(5px)" },
  "50%": { transform: "translateX(0px)" },
};

const bounceRight = {
  "0%, 100%": { transform: "translateX(-5px)" },
  "50%": { transform: "translateX(0px)" },
};

const bounceScaleY = {
  "0%, 100%": { transform: "scaleY(1.4) translateY(5px)" },
  "50%": { transform: "scaleY(1) translateY(0px)" },
};

const useStyles = makeStyles({
  root: {
    position: "absolute",
    pointerEvents: "none",
    zIndex: 999,
    opacity: 0,
    animationName: fadeIn,
    animationDuration: "0.5s",
    animationFillMode: "forwards",
    animationDelay: "0.9s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  bounceLeft: {
    animationName: [fadeIn, bounceLeft],
    animationDuration: "0.5s, 0.8s",
    animationFillMode: "forwards, none",
    animationDelay: "0.9s, 1.4s",
    animationTimingFunction: "ease, ease-in-out",
    animationIterationCount: "1, infinite",
  },
  bounceRight: {
    animationName: [fadeIn, bounceRight],
    animationDuration: "0.5s, 0.8s",
    animationFillMode: "forwards, none",
    animationDelay: "0.9s, 1.4s",
    animationTimingFunction: "ease, ease-in-out",
    animationIterationCount: "1, infinite",
  },
  bounceScaleVertical: {
    animationName: [fadeIn, bounceScaleY],
    animationDuration: "0.5s, 0.8s",
    animationFillMode: "forwards, none",
    animationDelay: "0.9s, 1.4s",
    animationTimingFunction: "ease, ease-in-out",
    animationIterationCount: "1, infinite",
  },
  svg: {
    width: "100%",
    height: "100%",
    overflow: "visible",
  },
});

/**
 * Use the start and end points to calculate the bounds and heading of the arrow.
 * (This is required to make the Arrow API convenient to use.)
 *
 * Constraints:
 *  - The arrow must either be horizontal or vertical other degrees are not supported.
 *  - The arrowhead is always at the end point, pointing either up, down, left, or right.
 *  - Either both X or both Y values are CSS calc() expressions, but the other pair must be a number with matching units.
 *
 * Algorithm:
 *  - Horizontal/vertical orientation is based on whether the X/Y coordinates of the start and end points are equal.
 *  - The direction of the arrowhead is determined by the non-equal coordinates of the start and end points. (The arrow will point in the direction of the larger one.)
 *
 * @param start
 * @param end
 * @param strokeWidth
 * @returns {{left: string, top: string, width: string, height: string, x1: string, y1: string, x2: string, y2: string}}
 */
function calculateArrowBounds(start, end, strokeWidth) {
  const isHorizontal = start.y === end.y;

  const getPos = (pos) => (typeof pos === "number" ? `${pos}px` : pos);

  const left = `min(${getPos(start.x)}, ${getPos(end.x)})`;
  const top = `min(${getPos(start.y)}, ${getPos(end.y)})`;
  const width = isHorizontal ? `calc(abs(${getPos(start.x)} - ${getPos(end.x)}))` : `${strokeWidth}px`;
  const height = isHorizontal ? `${strokeWidth}px` : `calc(abs(${getPos(start.y)} - ${getPos(end.y)}))`;

  const isRight =
    isHorizontal &&
    (parseFloat(start.x) < parseFloat(end.x) ||
      (start.x === "0" && end.x === "100%") ||
      (typeof start.x === "string" && start.x.includes("0") && end.x.includes("100%")));
  const isLeft =
    isHorizontal &&
    (parseFloat(start.x) > parseFloat(end.x) ||
      (start.x === "100%" && end.x === "0") ||
      (typeof start.x === "string" && start.x.includes("100%") && end.x.includes("0")));
  const isDown =
    !isHorizontal &&
    (parseFloat(start.y) < parseFloat(end.y) ||
      (start.y === "0" && end.y === "100%") ||
      (typeof start.y === "string" && start.y.includes("0") && end.y.includes("100%")));
  const isUp =
    !isHorizontal &&
    (parseFloat(start.y) > parseFloat(end.y) ||
      (start.y === "100%" && end.y === "0") ||
      (typeof start.y === "string" && start.y.includes("100%") && end.y.includes("0")));

  let x1, y1, x2, y2;
  if (isRight) {
    x1 = "0%";
    y1 = "50%";
    x2 = "100%";
    y2 = "50%";
  } else if (isLeft) {
    x1 = "100%";
    y1 = "50%";
    x2 = "0%";
    y2 = "50%";
  } else if (isDown) {
    x1 = "50%";
    y1 = "0%";
    x2 = "50%";
    y2 = "100%";
  } else if (isUp) {
    x1 = "50%";
    y1 = "100%";
    x2 = "50%";
    y2 = "0%";
  }
  return { left, top, width, height, x1, y1, x2, y2, isLeft, isDown, isRight, isUp, isHorizontal };
}

const Arrow = ({ start, end, color = "#003DAE", strokeWidth = 3 }) => {
  const styles = useStyles();
  const resizeCount = useResizeCount();
  let { left, top, width, height, x1, y1, x2, y2, isHorizontal, isLeft } = calculateArrowBounds(
    start,
    end,
    strokeWidth
  );
  const bounceClass = isHorizontal ? (isLeft ? styles.bounceLeft : styles.bounceRight) : styles.bounceScaleVertical;

  return (
    <div
      className={`${styles.root} ${bounceClass}`}
      style={{
        animationDelay: resizeCount ? ".3s, .3s" : undefined,
        left,
        top,
        width,
        height,
      }}
      key={resizeCount}
    >
      <svg className={styles.svg}>
        <defs>
          <marker id="arrowhead" markerWidth="6.67" markerHeight="4.67" refX="6" refY="2.33" orient="auto">
            <polygon points="0 0, 6.67 2.33, 0 4.67" fill={color} />
          </marker>
        </defs>
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={strokeWidth} markerEnd="url(#arrowhead)" />
      </svg>
    </div>
  );
};

Arrow.propTypes = {
  start: PropTypes.shape({
    x: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    y: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  }).isRequired,
  end: PropTypes.shape({
    x: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    y: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  }).isRequired,
  color: PropTypes.string,
  strokeWidth: PropTypes.number,
};

export default Arrow;
